// ─────────────────────────────────────────────────────────────────────────────
// Montagem do payload de envio ao MySuite.
//
// Esta é a regra de negócio mais delicada do Orion: o que o operador vê na
// tela vira, aqui, os códigos que o MySuite entende (status, operador de
// destino, prioridade, classificação, previsão). Errar um número aqui move o
// ticket para a fila errada.
//
// Na versão 1.x isso morava dentro do listener de clique do botão "Enviar
// Ticket" (renderer.js:4209-4420), lendo valor por valor do DOM. Aqui é uma
// função pura: recebe o estado da tela, devolve o payload. Nada de DOM — o que
// torna a regra testável sem abrir o app.
// ─────────────────────────────────────────────────────────────────────────────

import { STATUS, OPERADOR } from './codigosMysuite';
import { encontrarMelhorMatch } from './texto';
import { ajustarParaHorarioComercial } from './datas';
import { WEBHOOKS } from '../config/ambiente';
// A lista dos analistas de destino é usada aqui para resolver o NOME a partir
// do código escolhido; os três nomes seguem reexportados logo abaixo.
import { DESTINOS_SUPORTE_AVANCADO } from './codigosMysuite';

/** Tipos de atendimento oferecidos ao operador. */
export const TIPOS_ATENDIMENTO = [
  { valor: 'Conclusão', rotulo: 'Conclusão' },
  { valor: 'Atualização', rotulo: 'Atualização' },
  { valor: 'Aguardando interação de cliente', rotulo: 'Aguardando interação de cliente' },
  // O valor continua 'Setor de sistemas' (é o que o n8n espera); o rótulo é o
  // que o suporte usa no dia a dia.
  { valor: 'Setor de sistemas', rotulo: 'Solicitação não resolvida' },
  { valor: 'Suporte avançado', rotulo: 'Suporte avançado' },
  { valor: 'Comercial', rotulo: 'Comercial' },
  { valor: 'Na fila para retorno', rotulo: 'Na fila para retorno' },
  { valor: 'Contato sem sucesso', rotulo: 'Contato sem sucesso' },
];

/**
 * Os tipos da FORMALIZAÇÃO: os de cima mais o "Agendado".
 *
 * Agendado é o cliente pedindo para ser atendido em outro dia e horário: o
 * ticket vai para o status 104 com a previsão combinada, e a data e a hora são
 * obrigatórias (ver `faltaPrevisaoDoAgendado`). Fica fora de
 * TIPOS_ATENDIMENTO de propósito — o ticket novo e os tickets em massa usam
 * aquela lista com os seus próprios mapas de status, que não conhecem o 104.
 */
export const TIPOS_FORMALIZACAO = [
  ...TIPOS_ATENDIMENTO.slice(0, -1),
  { valor: 'Agendado', rotulo: 'Agendado' },
  TIPOS_ATENDIMENTO[TIPOS_ATENDIMENTO.length - 1],
];

/**
 * O que falta para enviar um "Agendado", ou '' quando está tudo certo.
 * Sem data e hora o 104 não tem para quando voltar; no passado, voltaria na hora.
 * `agora` é parâmetro para a regra poder ser testada fora do relógio.
 */
export function faltaPrevisaoDoAgendado(tipo, previsaoData, previsaoHora, agora = new Date()) {
  if (tipo !== 'Agendado') return '';
  if (!previsaoData || !previsaoHora) return 'Preencha a data e a hora do agendamento.';
  const [a, m, d] = previsaoData.split('-').map(Number);
  const [h, min] = previsaoHora.split(':').map(Number);
  if (new Date(a, m - 1, d, h, min) <= agora) return 'A data e a hora do agendamento já passaram.';
  return '';
}

/** Tipos em que o bloco de previsão não aparece. */
export const TIPOS_SEM_PREVISAO = [
  'Conclusão', 'Aguardando interação de cliente', 'Setor de sistemas',
  'Suporte avançado', 'Comercial', 'Contato sem sucesso',
];

// ── Códigos do MySuite ───────────────────────────────────────────────────────
// Deixados explícitos, com nome, porque números soltos no meio do código não
// dizem nada — e é justamente aqui que um engano manda o ticket para o lugar
// errado.

// Os códigos moram em codigosMysuite.js — sem dependências, porque valem
// também no ticket novo e nos tickets em massa, e as regras que os usam
// precisam poder ser testadas fora do app. Reexportados para nada quebrar.
export { STATUS, OPERADOR, DESTINOS_SUPORTE_AVANCADO } from './codigosMysuite';

// "Contato sem sucesso" tem valores fixos, que ignoram MySuite e IA.
const CONTATO_SEM_SUCESSO = {
  operador: OPERADOR.ANALISE_SUPORTE,
  classificacaoCodigo: 447,
  classificacaoNome: 'OUT - Contato Não Estabelecido',
  status: STATUS.AGENDADOS,
  prioridade: 1,
  criticidade: 13,
  minutosParaAgendar: 15,
};

/** Status final a partir do tipo de atendimento. */
function resolverStatus(tipo, ticketInfo) {
  const porTipo = {
    'Conclusão': STATUS.CONCLUSAO,
    'Aguardando interação de cliente': STATUS.AGUARDANDO_CLIENTE,
    'Setor de sistemas': STATUS.SETOR_SISTEMAS,
    'Suporte avançado': STATUS.SUPORTE_AVANCADO,
    'Comercial': STATUS.COMERCIAL,
    'Na fila para retorno': STATUS.FILA_RETORNO,
    'Agendado': STATUS.AGENDADOS,
  };
  if (porTipo[tipo]) return porTipo[tipo];

  // Sem regra própria: mantém o status que o ticket já tinha.
  if (ticketInfo?.codigoStatusTicket) return parseInt(ticketInfo.codigoStatusTicket, 10);
  if (ticketInfo?.codigoStatus) return parseInt(ticketInfo.codigoStatus, 10);
  return STATUS.EM_ATENDIMENTO;
}

/**
 * Operador de destino (transferência de fila).
 *
 * Suporte avançado vai para o analista nominal escolhido na validação, tenha ou
 * não a marcação de "possível falha de sistema" — quem informa o código é a
 * tela. A fila de Análise Suporte (106) é só o destino de segurança, para o
 * ticket não ficar sem dono quando nenhum analista veio.
 */
function resolverOperador(tipo, { destinoSuporteAvancado } = {}) {
  if (tipo === 'Setor de sistemas') return OPERADOR.SETOR_SISTEMAS;
  // Agendado volta para a fila geral: no horário combinado o MySuite passa o
  // ticket para "Aguardando Solução", e ele precisa estar na Análise Suporte
  // para quem estiver livre pegar — como no "Contato sem sucesso".
  if (tipo === 'Agendado') return OPERADOR.ANALISE_SUPORTE;
  if (tipo === 'Suporte avançado') {
    return destinoSuporteAvancado || OPERADOR.ANALISE_SUPORTE;
  }
  return OPERADOR.MANTER_ATUAL;
}

/** Prioridade a partir da criticidade escolhida. */
function resolverPrioridade(criticidade, ticketInfo) {
  let prioridade = parseInt(ticketInfo?.prioridade, 10) || 1;
  if (!criticidade) return prioridade;

  const desc = (criticidade.nome || '').toLowerCase();
  if (desc.includes('crític') || desc.includes('critic')) prioridade = 3;
  else if (desc.includes('alto') || desc.includes('alta')) prioridade = 2;
  return prioridade;
}

/**
 * Classificação final (nome + código).
 *
 * Prioridade, do mais fraco para o mais forte:
 *   1. o que já está no MySuite;
 *   2. a sugestão da IA — SÓ se o operador clicou em "Aplicar recomendação";
 *   3. o que o operador digitou no ajuste manual.
 */
function resolverClassificacao({ ticketInfo, classificacoesIa, iaAplicada, ajusteManual, listaClassificacoes }) {
  let nome = ticketInfo?.classificacao || '';
  let codigo = 0;

  if (ticketInfo) {
    const bruto = ticketInfo.codigoClassificacao
      ?? ticketInfo.classificacao_codigo
      ?? ticketInfo.codigo_classificacao;
    if (bruto !== undefined && bruto !== null) codigo = parseInt(bruto, 10) || 0;
  }

  if (iaAplicada && classificacoesIa) {
    if (classificacoesIa.classificacao) nome = classificacoesIa.classificacao;
    const bruto = classificacoesIa.codigoClassificacao ?? classificacoesIa.codigo_classificacao;
    if (bruto !== undefined && bruto !== null) codigo = parseInt(bruto, 10) || 0;
  }

  const manual = (ajusteManual || '').trim();
  if (manual) {
    nome = manual;
    // Resolve o código na lista do Supabase: primeiro por nome exato, depois
    // pela comparação tolerante.
    const exata = listaClassificacoes.find(c => c.nome.toLowerCase() === manual.toLowerCase());
    const achada = exata || encontrarMelhorMatch(
      manual,
      listaClassificacoes.map(c => ({ valor: String(c.codigo), texto: c.nome }))
    );
    if (achada) {
      codigo = parseInt(achada.codigo ?? achada.valor, 10) || 0;
      nome = achada.nome ?? achada.texto;
    }
  }

  return { nome, codigo };
}

/**
 * Código de ocorrência ou criticidade.
 * Usa o que o operador escolheu; se ele não escolheu, tenta casar o texto do
 * MySuite (ou o da IA, quando aplicada) com a lista do banco.
 */
function resolverCodigoDeLista(escolhido, { textoMySuite, textoIa, iaAplicada, lista }) {
  const codigo = parseInt(escolhido, 10) || 0;
  if (codigo) return codigo;

  const texto = textoMySuite || (iaAplicada ? textoIa : '') || '';
  if (!texto) return 0;

  const match = encontrarMelhorMatch(
    texto,
    lista.map(i => ({ valor: String(i.codigo), texto: i.nome }))
  );
  return match ? (parseInt(match.valor, 10) || 0) : 0;
}

/** Código do setor, com tradução do nome quando o código não vem. */
function resolverSetor(ticketInfo) {
  if (!ticketInfo) return 0;

  const codigo = parseInt(ticketInfo.codigoSetor, 10) || parseInt(ticketInfo.codigoSetorTicket, 10) || 0;
  if (codigo) return codigo;

  const setor = (ticketInfo.setor || '').trim().toUpperCase();
  if (setor === '1- SUPORTE') return 3;
  if (setor === 'AUTOMAÇÃO' || setor === 'AUTOMACAO') return 15;
  return 0;
}

/**
 * Aplica o analista de destino escolhido na confirmação do envio.
 *
 * Suporte avançado não vai para a fila 106: vai para um analista nominal,
 * escolhido na hora de confirmar — inclusive quando o atendimento está marcado
 * como possível falha de sistema. O payload leva o código E o nome, e o fluxo
 * do n8n usa o nome no relatório sem ter de traduzir o código.
 */
export function aplicarDestinoSuporteAvancado(payload, codigoDestino) {
  const cod = Number(codigoDestino) || 0;
  if (!cod) return payload;
  const nome = DESTINOS_SUPORTE_AVANCADO.find(d => d.codigo === cod)?.nome || null;
  return { ...payload, codigoOperador: cod, operadorDestinoNome: nome };
}

/**
 * Monta o payload de envio.
 *
 * Devolve { payload, url, resumo } — `resumo` é o que a tela de validação
 * mostra ao operador antes de confirmar (textos, não códigos).
 */
export function montarEnvio({
  numeroTicket,
  tipoAtendimento,
  interacao,
  ticketInfo,
  operador,
  // escolhas do operador
  ocorrenciaSelecionada,
  criticidadeSelecionada,
  ajusteAssunto,
  ajusteClassificacao,
  previsaoData,
  previsaoHora,
  destinoSuporteAvancado,
  // sugestão da IA
  classificacoesIa,
  iaAplicada,
  // listas de apoio
  listas,
}) {
  const { ocorrencias = [], criticidades = [], classificacoes = [] } = listas || {};
  const contatoSemSucesso = tipoAtendimento === 'Contato sem sucesso';

  // ── Classificação, ocorrência e criticidade ──
  let classificacao = resolverClassificacao({
    ticketInfo, classificacoesIa, iaAplicada,
    ajusteManual: ajusteClassificacao,
    listaClassificacoes: classificacoes,
  });

  let codOcorrencia = resolverCodigoDeLista(ocorrenciaSelecionada, {
    textoMySuite: ticketInfo?.ocorrencia,
    textoIa: classificacoesIa?.ocorrencia,
    iaAplicada,
    lista: ocorrencias,
  });

  let codCriticidade = resolverCodigoDeLista(criticidadeSelecionada, {
    textoMySuite: ticketInfo?.criticidade,
    textoIa: classificacoesIa?.criticidade,
    iaAplicada,
    lista: criticidades,
  });

  const ocorrenciaObj = ocorrencias.find(o => o.codigo === codOcorrencia);
  let criticidadeObj = criticidades.find(c => c.codigo === codCriticidade);

  let prioridade = resolverPrioridade(criticidadeObj, ticketInfo);
  let status = resolverStatus(tipoAtendimento, ticketInfo);
  let codOperador = resolverOperador(tipoAtendimento, { destinoSuporteAvancado });
  // Nome do analista de destino, para o fluxo não ter de traduzir o código.
  // Só existe no Suporte avançado com destino nominal — nos outros casos o
  // campo NÃO entra no payload, como no 1.4.1.
  const destinoNome = (tipoAtendimento === 'Suporte avançado' && destinoSuporteAvancado)
    ? (DESTINOS_SUPORTE_AVANCADO.find(d => d.codigo === Number(destinoSuporteAvancado))?.nome || null)
    : null;

  // ── Previsão ──
  let dataPrevisao = '0000-00-00';
  let horaPrevisao = '00:00:00';

  if (previsaoData) dataPrevisao = previsaoData;
  else if (ticketInfo?.dataPrevisao) dataPrevisao = ticketInfo.dataPrevisao;

  if (previsaoHora) horaPrevisao = previsaoHora.substring(0, 5);   // HH:MM, como o n8n espera
  else if (ticketInfo?.horaPrevisao) horaPrevisao = ticketInfo.horaPrevisao;

  // ── Regra fixa do "Contato sem sucesso" ──
  // Ignora MySuite e IA: operador, classificação, status, prioridade e
  // criticidade são fixos, e a previsão é agendada para daqui a 15 minutos,
  // sempre dentro do horário comercial.
  if (contatoSemSucesso) {
    codOperador = CONTATO_SEM_SUCESSO.operador;
    classificacao = {
      codigo: CONTATO_SEM_SUCESSO.classificacaoCodigo,
      nome: CONTATO_SEM_SUCESSO.classificacaoNome,
    };
    status = CONTATO_SEM_SUCESSO.status;
    prioridade = CONTATO_SEM_SUCESSO.prioridade;
    codCriticidade = CONTATO_SEM_SUCESSO.criticidade;
    criticidadeObj = criticidades.find(c => c.codigo === CONTATO_SEM_SUCESSO.criticidade);

    const agenda = new Date();
    agenda.setMinutes(agenda.getMinutes() + CONTATO_SEM_SUCESSO.minutosParaAgendar);
    const agendado = ajustarParaHorarioComercial(agenda);
    const pad = n => String(n).padStart(2, '0');
    dataPrevisao = `${agendado.getFullYear()}-${pad(agendado.getMonth() + 1)}-${pad(agendado.getDate())}`;
    horaPrevisao = `${pad(agendado.getHours())}:${pad(agendado.getMinutes())}`;
  }

  // ── Título ──
  const tituloAjustado = (ajusteAssunto || '').trim();
  const titulo = tituloAjustado || ticketInfo?.titulo || '';

  // ── Formato da previsão no envio ──
  // O MySuite grava a previsão em "dd/MM/aaaa" e "HH:MM" (ex.: 23/10/2026 e
  // 12:00). Mandada como "2026-10-23", ela era ignorada: no primeiro teste do
  // tipo "Agendado" o status mudou e a previsão ficou a antiga. Só o caminho do
  // OrionAtualiza troca de formato — o do "Contato sem sucesso" já agenda certo
  // com o formato antigo (o fluxo dele converte), e mexer ali o quebraria.
  // Sem previsão, continua o '0000-00-00' de sempre.
  let dataEnvio = dataPrevisao;
  let horaEnvio = horaPrevisao;
  if (!contatoSemSucesso && dataPrevisao !== '0000-00-00') {
    const [a, m, d] = dataPrevisao.split('-');
    dataEnvio = `${d}/${m}/${a}`;
    horaEnvio = String(horaPrevisao || '').substring(0, 5);
  }

  const payload = {
    codigoTicket: parseInt(numeroTicket, 10),
    interacao: interacao || 'Interação vazia.',
    codigoCriticidade: codCriticidade,
    codigoOcorrencia: codOcorrencia,
    codigoClassificacao: classificacao.codigo,
    classificacao: classificacao.nome,
    prioridade,
    dataPrevisao: dataEnvio,
    horaPrevisao: horaEnvio,
    codigoStatus: status,
    codigoSetor: resolverSetor(ticketInfo),
    codigoOperador: codOperador,
    titulo,
    operadorNome: operador?.nome || '',
    operadorEmail: operador?.email || '',
    ...(destinoNome ? { operadorDestinoNome: destinoNome } : {}),
  };

  // "Contato sem sucesso" tem webhook dedicada; os demais vão para OrionAtualiza.
  const url = contatoSemSucesso ? WEBHOOKS.contatoSemSucesso : WEBHOOKS.atualizarTicket;

  // O que o operador confere antes de confirmar — em texto, não em código.
  const resumo = {
    assunto: titulo || '—',
    classificacao: classificacao.nome || '—',
    ocorrencia: ocorrenciaObj?.nome
      || ticketInfo?.ocorrencia
      || (iaAplicada && classificacoesIa?.ocorrencia)
      || '—',
    criticidade: criticidadeObj?.nome
      || ticketInfo?.criticidade
      || (iaAplicada && classificacoesIa?.criticidade)
      || '—',
    tipoAtendimento: tipoAtendimento || '—',
    previsao: dataPrevisao !== '0000-00-00' ? `${dataPrevisao} ${horaPrevisao}` : '—',
    // A previsão some quando o tipo não tem previsão E nenhuma foi definida.
    // "Contato sem sucesso" está na lista sem-previsão mas ganha previsão
    // automática (+15 min), então continua aparecendo.
    mostrarPrevisao: !(TIPOS_SEM_PREVISAO.includes(tipoAtendimento) && dataPrevisao === '0000-00-00'),
    // O GUT só aparece no Suporte avançado com "possível falha de sistema".
    // "Solicitação não resolvida" não usa GUT.
    exigeGut: tipoAtendimento === 'Suporte avançado',
  };

  return { payload, url, resumo };
}

/**
 * Texto da interação de "Contato sem sucesso".
 * Cabeçalho fixo em negrito e uma linha por tentativa (telefone - o que ocorreu).
 */
export function montarInteracaoContato(tentativas) {
  const linhas = tentativas
    .filter(t => t.telefone.trim() && t.motivo.trim())
    .map(t => `${t.telefone.trim()} - ${t.motivo.trim()}`);
  return ['<b>Tentativa de contato sem sucesso</b>:', ...linhas].join('\n');
}

/** true quando alguma tentativa foi preenchida pela metade. */
export function tentativasIncompletas(tentativas) {
  return tentativas.some(t => {
    const tel = t.telefone.trim();
    const mot = t.motivo.trim();
    return (tel && !mot) || (!tel && mot);
  });
}
